import React, { useState, useEffect } from 'react';
import { DictionaryInfo } from '../types';
import { upsertWordDefinition } from '../repository/adminRepository';

const ADMIN_USERNAMES = ['nate', 'lisie'];

function getIsAdmin(): boolean {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    return ADMIN_USERNAMES.includes((user.username || '').toLowerCase());
  } catch {
    return false;
  }
}

interface WordInsightProps {
  info?: DictionaryInfo | null;
  loading?: boolean;
  lastGuessedWord?: string | null;
}

const WordInsight: React.FC<WordInsightProps> = ({ info, loading, lastGuessedWord }) => {
  const [editing, setEditing] = useState(false);
  const [defText, setDefText] = useState('');
  const [etyText, setEtyText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const isAdmin = getIsAdmin();

  useEffect(() => {
    setEditing(false);
    setSaveError(null);
  }, [info]);

  const handleEdit = () => {
    setDefText(info?.definition || '');
    setEtyText(info?.etymology || '');
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!info) return;
    setSaving(true);
    setSaveError(null);
    try {
      await upsertWordDefinition(info.word, defText, etyText);
      info.definition = defText;
      info.etymology = etyText;
      setEditing(false);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="panel">
        <h3>Word Insight</h3>
        {lastGuessedWord && <div className="badge">{lastGuessedWord}</div>}
        <div className="insight-loading">
          <div className="spinner" />
          <span>Looking up definition...</span>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="panel">
        <h3>Word Insight</h3>
        <p className="app-subtitle">Submit a guess to see its meaning.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Word Insight</h3>
        {isAdmin && !editing && (
          <button className="secondary-button" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={handleEdit}>
            Edit
          </button>
        )}
      </div>
      <div className="badge" style={{ marginTop: 8 }}>{info.word}</div>

      {editing ? (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem' }}>Definition</label>
            <textarea
              rows={3}
              value={defText}
              onChange={(e) => setDefText(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem' }}>Origin</label>
            <textarea
              rows={3}
              value={etyText}
              onChange={(e) => setEtyText(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
          {saveError && <div className="notice error">{saveError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary-button" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="secondary-button" style={{ padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p style={{ fontSize: '1rem', marginTop: '12px' }}>
            <strong>{info.part_of_speech || 'Definition'}:</strong> {info.definition}
          </p>
          {info.example && (
            <p style={{ fontFamily: 'Source Serif 4, serif', color: 'var(--theme-muted)' }}>
              "{info.example}"
            </p>
          )}
          {info.etymology && (
            <p style={{ fontFamily: 'Source Serif 4, serif', marginTop: '12px' }}>
              <strong>Origin:</strong> {info.etymology}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default WordInsight;
